import React, { useEffect, useState, useRef, useContext, useCallback, useMemo } from 'react';
import {
    TextField,
    Autocomplete,
    Tooltip,
    IconButton,
    Portal,
    Button,
    Paper,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    TableCell,
    Popper
} from '@mui/material';
import debounce from 'lodash/debounce'
import Draggable from 'react-draggable';
import CustomDatePicker from '../../Widgets/CustomDates/CustomDatePicker/CustomDatePicker';
import DeleteIcon from '@mui/icons-material/Delete';
import CancelIcon from '@mui/icons-material/Cancel';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { styled } from '@mui/system';
import { PermissionsContext } from '../../../Contexts/Permissions';
import AddAccountCardModal from '../../Widgets/AddAccountCardModal/AddAccountCardModal';
import Constants from '../../../assests/Constants/constants';
import useCaseSystemAccountSettings from '../../../hooks/useCaseSystemAccountSettings';
import { getPaymentAcceptanceLineAccounts } from '../../../Utils/paymentAcceptanceLineAccounts';
import CustomMonthDatePicker from '../../Widgets/CustomDates/CustomMonthDatePicker/CustomMonthDatePicker';
import FixedSizeVirtualizedListbox from '../../Widgets/AutocompleteCardSelect/FixedSizeVirtualizedListbox';
import dayjs from 'dayjs';
import ForeignCurrencyService from '../../../Services/foreignCurrency.service';
import SubAccountantService from '../../../Services/subAccountant.service';
import { toast } from 'react-toastify';
import Iframe from 'react-iframe';
import NoDocumentsPdf from "../../../assests/pdf/NoDocument.pdf";
import FilePreviewCarousel from './FilePreviewCarousel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { beautifyNumber } from '../../../Utils/FormatNumber';
import PaymentAmountsSummaryBar, { computePaymentAmounts } from './PaymentAmountsSummaryBar';
import PaymentWithholdingTaxRow, { usePaymentWithholdingRow, readDeductionRate } from './PaymentWithholdingTaxRow';
import PaymentPartialAmountPanel, {
    PARTIAL_PAYMENT_NOTE_MARKER,
    buildPartialPaymentNote,
    computeOpenBalance,
    rescaleLineAmounts,
} from './PaymentPartialAmountPanel';
import DocumentService from '../../../Services/documents.service';
import '../../EditModal/EditModal.css';
import fieldStyles from '../../JournalEntryies/SubComponents/AddJournalEntryModal.module.scss';
import paStyles from './PaymentAcceptanceCreateEditModal.module.scss';

// Build the duplicate indicator prefix: upload number + the source report month beside it (e.g. "שוכפל מ 44 06/2026")
const buildDuplicatePrefix = (uploadNumber, reportMonthDate) => {
    const reportMonthLabel = reportMonthDate && dayjs(reportMonthDate).isValid()
        ? dayjs(reportMonthDate).format('MM/YYYY')
        : '';
    return reportMonthLabel
        ? `שוכפל מ ${uploadNumber} ${reportMonthLabel}`
        : `שוכפל מ ${uploadNumber}`;
};

const toAmount = (raw) => {
    const n = typeof raw === 'string' ? parseFloat(raw) : raw;
    return Number.isFinite(n) ? n : 0;
};

const StyledListItem = styled('li')({
    color: '#000000',
    '&:hover': {
        cursor: 'pointer',
    },
});

const ComboPopper = React.forwardRef(({ direction = 'rtl', ...props }, ref) => (
    <Popper
        {...props}
        ref={ref}
        dir={direction}
        className={`${props.className || ''} ${fieldStyles.comboPopper} edit-modal-combo-popper ${paStyles.comboMenuBlack}`}
        placement={direction === 'rtl' ? 'bottom-end' : 'bottom-start'}
        style={{ ...props.style, zIndex: 1600, minWidth: props.style?.minWidth || 280 }}
    />
));
ComboPopper.displayName = 'ComboPopper';

function AddAccountCardOptionContent({ label, searchTerm }) {
    const text = String(label || '').trim();
    return (
        <div className="edit-add-account-card" style={{ color: '#304FFF' }}>
            <span className="edit-add-account-card__plus" aria-hidden="true" style={{ color: '#304FFF' }}>+</span>
            <span className="edit-add-account-card__label" style={{ color: '#304FFF' }}>
                {text}
                {searchTerm ? ` "${searchTerm}"` : ''}
            </span>
        </div>
    );
}

const newEntryTooltipSx = {
    '& .MuiTooltip-tooltip': {
        maxWidth: 420,
        fontSize: '1.25rem',
        fontWeight: 700,
        backgroundColor: '#1a237e',
        color: '#fff',
    },
    '& .MuiTooltip-arrow': { color: '#1a237e' },
};

const clearTooltipComponentsProps = {
    tooltip: {
        sx: {
            maxWidth: 420,
            fontSize: '1.25rem',
            fontWeight: 700,
            backgroundColor: '#1a237e',
            color: '#fff',
            padding: '8px 14px',
            lineHeight: 1.35,
        },
    },
    arrow: {
        sx: { color: '#1a237e' },
    },
};

const ClearTooltip = ({ title, children, placement = 'top', ...rest }) => (
    <Tooltip
        title={title}
        placement={placement}
        arrow
        PopperProps={{ sx: newEntryTooltipSx }}
        componentsProps={clearTooltipComponentsProps}
        {...rest}
    >
        {children}
    </Tooltip>
);

const formatTooltipDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return String(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    return `${day}/${month}/${d.getFullYear()}`;
};

const StyledTableContainer = styled(TableContainer)({
    width: '100%',
    boxShadow: 'none',
    borderRadius: 0,
    maxHeight: '400px',
    overflowY: 'auto',
    background: 'transparent',
});

const StyledTable = styled(Table)({
    borderCollapse: 'collapse',
    tableLayout: 'fixed',
    width: '100%',
});

const StyledTableHead = styled(TableHead)({
    backgroundColor: '#E3E7FE',
    '& .MuiTableCell-head': {
        backgroundColor: '#E3E7FE',
        color: '#1222b3',
        fontFamily: 'var(--xtram-font, inherit)',
        fontSize: '12px',
        fontWeight: 700,
        textAlign: 'center',
        textTransform: 'none',
        borderBottom: '1px solid #e5e7f6',
        position: 'sticky',
        top: 0,
        padding: '10px 8px',
        zIndex: 2,
    },
});

const StyledTableRow = styled(TableRow)({
    '&:hover': {
        backgroundColor: 'rgba(48, 79, 255, 0.04)',
        transition: 'background-color 0.2s ease',
    },
});

const StyledTableCellHeader = styled(TableCell)({
    backgroundColor: '#E3E7FE',
    color: '#1222b3',
    fontFamily: 'var(--xtram-font, inherit)',
    fontSize: '12px',
    fontWeight: 700,
    textAlign: 'center',
    textTransform: 'none',
    borderBottom: '1px solid #e5e7f6',
    position: 'sticky',
    top: 0,
    padding: '10px 8px',
    zIndex: 2,
});

const StyledTableCell = styled(TableCell)({
    borderTop: '1px solid #eef0f8',
    backgroundColor: '#fff',
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    textAlign: 'center',
    padding: '8px 6px',
    fontSize: '13px',
    fontFamily: 'var(--xtram-font, inherit)',
    position: 'relative',
    verticalAlign: 'middle',
    '&:hover': {
        backgroundColor: 'rgba(48, 79, 255, 0.04)',
    },
});

const PaymentAcceptanceDuplicateModal = ({
    open,
    onClose,
    sourceItem,
    onSave,
    t,
    accountCardOptions,
    client,
    setLoading,
    token,
    direction,
    sortDate,
    user,
    isClosedReportingMonth,
    isRange,
    fetchEnums
}) => {
    const { settings: caseSystemSettings } = useCaseSystemAccountSettings(client?.caseId, token);
    const { userTypeContext, permissionsState } = useContext(PermissionsContext);

    const nodeRef = useRef(null);
    /** When true, we've already hydrated from sourceItem for this open session — don't reset on parent re-renders (e.g. EditModal passes a new object every render). */
    const duplicateHydratedForOpenRef = useRef(false);
    const [container] = useState(() => document.createElement('div'));
    // Show/hide PDF iFrame
    const [showPdfIframe, setShowPdfIframe] = useState(true);
    const [docIndex, setDocIndex] = useState(0);

    /** Total of the document being duplicated — the full amount a partial payment is taken out of. */
    const [documentTotal, setDocumentTotal] = useState(() => toAmount(sourceItem?.amount));
    /** Sum of the receipts/payments already registered against the source document. */
    const [alreadyPaid, setAlreadyPaid] = useState(0);
    const [isPartialPayment, setIsPartialPayment] = useState(false);

    // Initialize duplicateItem from sourceItem
    const [duplicateItem, setDuplicateItem] = useState(() => {
        if (!sourceItem) return null;
        
        // Description should already have duplicate indicator from handleOpenDuplicate
        // But ensure it's there if missing
        const sourceUploadNumber = sourceItem.sourceUploadNumber || sourceItem.uploadNumber;
        const sourceReportingMonthDate = sourceItem.sourceReportingMonthDate || sourceItem.reportingMonthDate || null;
        let description = sourceItem.description || '';
        
        // Check if description already contains duplicate indicator
        const hasDuplicateIndicator = description.includes('שוכפל מ');
        
        if (sourceUploadNumber && !hasDuplicateIndicator) {
            const duplicatePrefix = buildDuplicatePrefix(sourceUploadNumber, sourceReportingMonthDate);
            if (description) {
                description = `${duplicatePrefix} – ${description}`;
            } else {
                description = duplicatePrefix;
            }
        }
        
        // Deep copy the source item
        const duplicated = {
            ...sourceItem,
            id: undefined,
            uploadNumber: undefined,
            uploadDate: new Date(),
            description: description,
            sourceUploadNumber: sourceUploadNumber, // Store source upload number
            sourceReportingMonthDate: sourceReportingMonthDate, // Store source report month for the duplicate indicator
            checks: sourceItem.checks ? sourceItem.checks.map(c => ({ ...c })) : [],
            creditCards: sourceItem.creditCards ? sourceItem.creditCards.map(c => ({ ...c })) : [],
            cash: sourceItem.cash ? sourceItem.cash.map(c => ({ ...c })) : [],
            bankTransactions: sourceItem.bankTransactions ? sourceItem.bankTransactions.map(c => ({ ...c })) : [],
            offsets: sourceItem.offsets ? sourceItem.offsets.map(c => ({ ...c })) : [],
        };
        return duplicated;
    });

    // Hydrate from sourceItem only once per open session (EditModal passes a new object every render).
    useEffect(() => {
        if (!open) {
            duplicateHydratedForOpenRef.current = false;
            return;
        }
        if (!sourceItem) return;

        if (duplicateHydratedForOpenRef.current) {
            return;
        }
        duplicateHydratedForOpenRef.current = true;

        const sourceUploadNumber = sourceItem.sourceUploadNumber || sourceItem.uploadNumber;
        const sourceReportingMonthDate = sourceItem.sourceReportingMonthDate || sourceItem.reportingMonthDate || null;
        let description = sourceItem.description || '';

        const hasDuplicateIndicator = description.includes('שוכפל מ');

        if (sourceUploadNumber && !hasDuplicateIndicator) {
            const duplicatePrefix = buildDuplicatePrefix(sourceUploadNumber, sourceReportingMonthDate);
            if (description) {
                description = `${duplicatePrefix} – ${description}`;
            } else {
                description = duplicatePrefix;
            }
        }

        const duplicated = {
            ...sourceItem,
            id: undefined,
            uploadNumber: undefined,
            uploadDate: new Date(),
            description: description,
            sourceUploadNumber: sourceUploadNumber,
            sourceReportingMonthDate: sourceReportingMonthDate,
            checks: sourceItem.checks ? sourceItem.checks.map(c => ({ ...c })) : [],
            creditCards: sourceItem.creditCards ? sourceItem.creditCards.map(c => ({ ...c })) : [],
            cash: sourceItem.cash ? sourceItem.cash.map(c => ({ ...c })) : [],
            bankTransactions: sourceItem.bankTransactions ? sourceItem.bankTransactions.map(c => ({ ...c })) : [],
            offsets: sourceItem.offsets ? sourceItem.offsets.map(c => ({ ...c })) : [],
        };
        setDuplicateItem(duplicated);
        setDocumentTotal(toAmount(sourceItem.amount));
        setAlreadyPaid(0);
        setIsPartialPayment(false);
    }, [sourceItem, open]);

    // What earlier receipts/payments already covered of the source Income/Expense, so the
    // partial payment is offered against the open balance rather than the document total.
    // The link is either the document this modal was opened from (EditModal) or, when an
    // existing payment is duplicated, the invoice that payment itself points at.
    const relatedSourceIds = useMemo(() => {
        if (!duplicateItem) return null;
        const { sourceDocumentId, sourceDocType, relatedIncomeId, relatedExpenseId } = duplicateItem;
        if (sourceDocumentId && sourceDocType === 'Incomes') return { incomeId: sourceDocumentId };
        if (sourceDocumentId && sourceDocType === 'Expenses') return { expenseId: sourceDocumentId };
        if (relatedIncomeId) return { incomeId: relatedIncomeId };
        if (relatedExpenseId) return { expenseId: relatedExpenseId };
        return null;
    }, [duplicateItem]);
    const relatedSourceKey = relatedSourceIds ? JSON.stringify(relatedSourceIds) : '';

    useEffect(() => {
        if (!open || !token || !client?.caseId || !relatedSourceKey) return;

        let cancelled = false;
        const fetchAlreadyPaid = async () => {
            try {
                const ids = JSON.parse(relatedSourceKey);
                const response = await DocumentService.GetPaymentAcceptancesByRelatedSource(client.caseId, ids, token);
                if (!response.ok || cancelled) return;
                const data = await response.json();
                if (cancelled) return;
                const total = (Array.isArray(data) ? data : []).reduce((sum, payment) => sum + toAmount(payment?.amount), 0);
                setAlreadyPaid(Number(total.toFixed(2)));
            } catch (error) {
                console.error('Failed to fetch related payments for partial payment', error);
            }
        };
        fetchAlreadyPaid();

        return () => { cancelled = true; };
    }, [open, token, client?.caseId, relatedSourceKey]);

    const [searchTerm, setSearchTerm] = useState('');
    const [cardIdOption, setCardIdOption] = useState(-1);
    const [addCardContext, setAddCardContext] = useState(null);
    const [newAccountCard, setNewAccountCard] = useState({
        accountCodeName: '',
        accountCodeId: '',
        accountCaseId: '',
    });
    const [addNewAccountCardDocument, setAddNewAccountCardDocument] = useState(false);
    const [showAccountCardInputsEdit, setShowAccountCardInputsEdit] = useState(false);
    const [palCode, setPalCode] = useState('');
    const [newCardId, setNewCardId] = useState(null);
    const [subAccountantNewOptions, setSubAccountantNewOptions] = useState([]);

    const fetchSubAccountants = async () => {
        if (!client?.accountantId) {
            return;
        }
        const result2 = await SubAccountantService.GetSubAccountantByAccountantId(token, client?.accountantId);
        const accountantResult = await SubAccountantService.GetAccountantById(token, client?.accountantId);
        const enhancedAccountantResult = {
            ...accountantResult,
            subAccountantId: accountantResult.accountantId,
        };

        if (result2) {
            const combinedSubAccountants = [enhancedAccountantResult, ...result2];
            if (client) {
                combinedSubAccountants.push({
                    subAccountantId: client.caseId,
                    name: client.name,
                });
            }
            const combinedOptions = combinedSubAccountants.map((acc) => ({
                value: acc.subAccountantId,
                label: acc.name,
            }));
            setSubAccountantNewOptions(combinedOptions);
        }
    };

    useEffect(() => {
        fetchSubAccountants();
    }, [client, token]);

    // Flatten all items
    const combinedItems = React.useMemo(() => {
        if (!duplicateItem) return [];
        const checks = (duplicateItem?.checks || []).map((chk, idx) => ({ ...chk, itemType: 'checks', itemTypeIndex: idx }));
        const ccs = (duplicateItem?.creditCards || []).map((cc, idx) => ({ ...cc, itemType: 'creditCards', itemTypeIndex: idx }));
        const csh = (duplicateItem?.cash || []).map((c, idx) => ({ ...c, itemType: 'cash', itemTypeIndex: idx }));
        const bts = (duplicateItem?.bankTransactions || []).map((bt, idx) => ({ ...bt, itemType: 'bankTransactions', itemTypeIndex: idx }));
        const ofs = (duplicateItem?.offsets || []).map((of, idx) => ({ ...of, itemType: 'offsets', itemTypeIndex: idx }));
        return [...checks, ...ccs, ...csh, ...bts, ...ofs];
    }, [duplicateItem]);

    // Flatten all relevant doc URLs, ignoring null/empty strings.
    const allDocUrls = React.useMemo(() => {
        if (!duplicateItem) return [];
        const docArray = [];
        // push only if not null/undefined/empty
        if (duplicateItem?.receiptDocumentUrl) {
            docArray.push(duplicateItem.receiptDocumentUrl);
        }

        // For each check
        duplicateItem?.checks?.forEach((check) => {
            if (check.documentUrl) {
                docArray.push(check.documentUrl);
            }
        });

        duplicateItem?.creditCards?.forEach((creditCard) => {
            if (creditCard.documentUrl) {
                docArray.push(creditCard.documentUrl);
            }
        });

        duplicateItem?.cash?.forEach((cash) => {
            if (cash.documentUrl) {
                docArray.push(cash.documentUrl);
            }
        });

        duplicateItem?.bankTransactions?.forEach((bankTransaction) => {
            if (bankTransaction.documentUrl) {
                docArray.push(bankTransaction.documentUrl);
            }
        });

        duplicateItem?.offsets?.forEach((offset) => {
            if (offset.documentUrl) {
                docArray.push(offset.documentUrl);
            }
        });

        return docArray;
    }, [duplicateItem]);

    const handleToggleIframe = () => {
        setShowPdfIframe((prev) => !prev);
    };

    // Foreign currency recalculation
    const recalcAmount = async (itemType, idx, paymentDate, foreignCurrencyAmount, code = null) => {
        if (
            duplicateItem.foreignCurrencyCode == null ||
            !paymentDate ||
            !foreignCurrencyAmount
        ) {
            return;
        }

        try {
            const rate = await ForeignCurrencyService.GetIlsRateForDate(
                code != null ? code : duplicateItem.foreignCurrencyCode,
                paymentDate
            );
            const amount = Number((foreignCurrencyAmount * rate).toFixed(2));
            handleItemChange(itemType, idx, 'amount', amount);
        } catch (err) {
            console.error('Failed to recalc FC amount', err);
            toast.error(t('CurrencyRateFetchError'));
        }
    };

    const debouncedRecalc = useRef(
        debounce((itemType, idx, paymentDate, foreignCurrencyAmount) => {
            recalcAmount(itemType, idx, paymentDate, foreignCurrencyAmount)
        }, 500)
    ).current;

    const handleForeignAmountChange = (itemType, idx, newForeignAmount) => {
        if (newForeignAmount === '') {
            handleItemChange(itemType, idx, 'foreignCurrencyAmount', null);
            return;
        }
        if (
            duplicateItem.foreignCurrencyCode === null ||
            duplicateItem.foreignCurrencyCode === '' ||
            duplicateItem.foreignCurrencyCode === undefined
        ) {
            toast.error(t('PleaseSelectCurrencyCodeFirst'));
            return;
        }
        handleItemChange(itemType, idx, 'foreignCurrencyAmount', newForeignAmount);
        const item = duplicateItem[itemType]?.[idx];
        debouncedRecalc(itemType, idx, item?.paymentDate, newForeignAmount);
    };

    const handlePaymentDateChange = (itemType, idx, newDate) => {
        if (!newDate) {
            return;
        }
        handleItemChange(itemType, idx, 'paymentDate', newDate);
        const foreignCurrencyAmount = duplicateItem[itemType]?.[idx]?.foreignCurrencyAmount;
        recalcAmount(itemType, idx, newDate, foreignCurrencyAmount);
    };

    const handleCurrencyCodeChange = (newCode) => {
        setDuplicateItem(prev => ({ ...prev, foreignCurrencyCode: newCode }));
        if (newCode === null || newCode === '' || newCode === undefined) {
            ['checks', 'creditCards', 'cash', 'bankTransactions', 'offsets'].forEach(type => {
                (duplicateItem[type] || []).forEach((item, idx) => {
                    handleItemChange(type, idx, 'amount', null);
                    handleItemChange(type, idx, 'foreignCurrencyAmount', null);
                });
            });
            return;
        }
        ['checks', 'creditCards', 'cash', 'bankTransactions', 'offsets'].forEach(type => {
            (duplicateItem[type] || []).forEach((item, idx) => {
                recalcAmount(type, idx, item.paymentDate, item.foreignCurrencyAmount, newCode);
            });
        });
    };

    // Reverse calculation: amount -> foreignCurrencyAmount
    const debouncedRecalcReverse = useMemo(() =>
        debounce(async (itemType, idx, amount, paymentDate, codeOverride) => {
            if (!paymentDate || amount === '' || isNaN(Number(amount))) return;
            const code = codeOverride ?? duplicateItem.foreignCurrencyCode;
            if (code === null || code === '' || code === undefined) return;
            try {
                const rate = await ForeignCurrencyService.GetIlsRateForDate(code, paymentDate);
                const foreignAmt = Number((Number(amount) / rate).toFixed(2));
                handleItemChange(itemType, idx, 'foreignCurrencyAmount', foreignAmt);
            } catch (err) {
                console.error('Failed to recalc reverse FC amount', err);
                toast.error(t('CurrencyRateFetchError'));
            }
        }, 500)
        , [duplicateItem?.foreignCurrencyCode]);

    const handleAmountChange = useCallback((itemType, idx, newAmount) => {
        if (newAmount === '') {
            handleItemChange(itemType, idx, 'amount', null);
            return;
        }
        handleItemChange(itemType, idx, 'amount', newAmount);
        const item = duplicateItem[itemType]?.[idx];
        const paymentDate = item?.paymentDate;
        const currencyCode = duplicateItem.foreignCurrencyCode;
        debouncedRecalcReverse(
            itemType,
            idx,
            newAmount,
            paymentDate,
            currencyCode
        );
    }, [duplicateItem, debouncedRecalcReverse]);

    // Item management
    const handleItemChange = (type, index, field, value) => {
        setDuplicateItem(prevItem => {
            const updatedTypeItems = prevItem[type].map((item, idx) => {
                if (idx === index) {
                    return { ...item, [field]: value };
                }
                return item;
            });

            // Update credit/debit accounts based on paymentAcceptanceType change
            const paymentType = duplicateItem.paymentAcceptanceType;
            if (field === 'paymentAcceptanceType' || (paymentType && (field === 'creditAccount' || field === 'debitAccount'))) {
                // When paymentAcceptanceType changes, update all items' accounts
                if (field === 'paymentAcceptanceType') {
                    const newPaymentType = value;
                    updatedTypeItems.forEach((item, idx) => {
                        if (newPaymentType === 'תקבול') {
                            item.creditAccount = duplicateItem.accountCodeId || '';
                            item.debitAccount = item.debitAccount || '';
                        } else {
                            item.debitAccount = duplicateItem.accountCodeId || '';
                            item.creditAccount = item.creditAccount || '';
                        }
                    });
                }
            }

            return { ...prevItem, [type]: updatedTypeItems };
        });
    };

    const handleAddItem = (theType) => {
        const accounts = getPaymentAcceptanceLineAccounts({
            paymentType: duplicateItem.paymentAcceptanceType,
            itemType: theType,
            partyAccountId: duplicateItem.accountCodeId,
            systemAccounts: caseSystemSettings,
        });

        const newItem = {
            type: theType,
            description: '',
            amount: amounts.suggestedLineAmount,
            paymentDate: duplicateItem.date ? new Date(duplicateItem.date) : new Date(),
            bankName: '',
            bankCode: '',
            bankBranchCode: '',
            supplierAccountNumber: '',
            bankTransactionsCode: caseSystemSettings.generalBankCardId,
            ...accounts,
        };

        setDuplicateItem(prevItem => ({
            ...prevItem,
            [theType]: Array.isArray(prevItem[theType]) ? [...prevItem[theType], newItem] : [newItem]
        }));
    };

    const handleRemoveItem = (type, index) => {
        setDuplicateItem(prevItem => {
            if (!prevItem[type] || !Array.isArray(prevItem[type])) {
                return prevItem;
            }
            const updatedTypeItems = [...prevItem[type]];
            updatedTypeItems.splice(index, 1);
            return { ...prevItem, [type]: updatedTypeItems };
        });
    };

    const handleDuplicateItemChange = (name, value) => {
        // If changing description, ensure duplicate indicator is preserved if it exists
        if (name === 'description') {
            const sourceUploadNumber = duplicateItem.sourceUploadNumber;
            const sourceReportingMonthDate = duplicateItem.sourceReportingMonthDate || duplicateItem.reportingMonthDate || null;
            let newDescription = value || '';
            
            // Check if the new description already has the duplicate indicator
            const hasDuplicateIndicator = newDescription.includes('שוכפל מ');
            
            // If user removed the duplicate indicator, add it back
            if (sourceUploadNumber && !hasDuplicateIndicator) {
                const duplicatePrefix = buildDuplicatePrefix(sourceUploadNumber, sourceReportingMonthDate);
                if (newDescription) {
                    newDescription = `${duplicatePrefix} – ${newDescription}`;
                } else {
                    newDescription = duplicatePrefix;
                }
            }
            
            setDuplicateItem(prev => ({ ...prev, [name]: newDescription }));
        } else {
            setDuplicateItem(prev => ({ ...prev, [name]: value }));
        }
        
        // When PaymentAcceptanceType changes, update all items' accounts
        if (name === 'paymentAcceptanceType') {
            ['checks', 'creditCards', 'cash', 'bankTransactions', 'offsets'].forEach(type => {
                (duplicateItem[type] || []).forEach((item, idx) => {
                    if (value === 'תקבול') {
                        handleItemChange(type, idx, 'creditAccount', duplicateItem.accountCodeId || '');
                    } else {
                        handleItemChange(type, idx, 'debitAccount', duplicateItem.accountCodeId || '');
                    }
                });
            });
        }
    };

    const openBalance = useMemo(
        () => computeOpenBalance(documentTotal, alreadyPaid),
        [documentTotal, alreadyPaid]
    );

    /**
     * The paid amount is the amount of this receipt/payment, so the existing payment lines
     * are re-split to add up to it — a duplicate of a 1,000 document paid 200 keeps its
     * lines, scaled down to 200.
     */
    const handlePaidAmountChange = (nextAmount) => {
        setDuplicateItem(prevItem => {
            if (!prevItem) return prevItem;
            const withNewGross = { ...prevItem, amount: nextAmount == null ? '' : nextAmount };
            const net = Number((toAmount(withNewGross.amount) - toAmount(prevItem.withholdingTax)).toFixed(2));
            return rescaleLineAmounts(withNewGross, net);
        });
    };

    const handlePartialToggle = (enabled) => {
        setIsPartialPayment(enabled);
        // Turning it on clears the amount so the user types how much is being paid now;
        // turning it off puts the whole document back.
        handlePaidAmountChange(enabled ? null : documentTotal);
    };

    const formattedAccountCardOptions = accountCardOptions?.map((option) => ({
        value: option.cardId,
        label: option.name,
        name: option.name,
        accountCaseId: option.accountCaseId || '',
        cardId: option.cardId,
        value1: option.value1,
        palCode: option.palCode,
        isEditable: option.isEditable,
        externalSoftwareCardNumber: option.externalSoftwareCardNumber || '',
    })) || [];

    const filterAccountCardOptions = useCallback((options, state) => {
        const input = (state.inputValue || '').trim();
        if (!input) return options;
        const inputLower = input.toLowerCase();
        return options.filter((opt) => {
            if (opt.value1 === 'AddItem') return true;
            const cardIdStr = String(opt.cardId ?? opt.value ?? '');
            const matchCardId = cardIdStr.toLowerCase().includes(inputLower) || cardIdStr.includes(input);
            const matchLabel = (opt.label || '').toLowerCase().includes(inputLower);
            const matchName = (opt.name || '').toLowerCase().includes(inputLower);
            const matchPalCode = (opt.palCode != null && String(opt.palCode).toLowerCase().includes(inputLower));
            const extId = (opt.externalSoftwareCardNumber || '').trim();
            const matchExternalSoftwareCardId = extId !== '' && extId.toLowerCase().includes(inputLower);
            return matchCardId || matchLabel || matchName || matchPalCode || matchExternalSoftwareCardId;
        });
    }, []);

    useEffect(() => {
        document.body.appendChild(container);
        return () => {
            try {
                document.body.removeChild(container);
            } catch (err) {
                // ignore
            }
        };
    }, [container]);

    useEffect(() => {
        if (accountCardOptions && accountCardOptions.length > 0) {
            const maxCardId = Math.max(...accountCardOptions.slice(1).map((option) => parseInt(option.cardId, 10)));
            setCardIdOption(maxCardId + 1);
        }
    }, [accountCardOptions]);

    const handleChangeAfterAdd = (cardId, type) => {
        setNewCardId({ cardId, type });
    };

    useEffect(() => {
        if (accountCardOptions && accountCardOptions.length > 0 && newCardId) {
            const selectedOption = accountCardOptions.find((opt) => opt.cardId === newCardId.cardId);
            if (selectedOption) {
                if (addCardContext && addCardContext.targetField) {
                    handleItemChange(
                        addCardContext.itemType,
                        addCardContext.idx,
                        addCardContext.targetField,
                        selectedOption.cardId
                    );
                } else {
                    const formattedSelected = formattedAccountCardOptions?.find(
                        (opt) => opt.cardId === selectedOption.cardId || opt.value === selectedOption.cardId
                    );
                    if (formattedSelected) {
                        handleDuplicateItemChange('accountCodeId', formattedSelected.value);
                        handleDuplicateItemChange('accountCodeName', formattedSelected.name);
                        handleDuplicateItemChange('accountCaseId', formattedSelected.accountCaseId);
                    }
                }
            }
            setAddNewAccountCardDocument(false);
            setNewCardId(null);
            setAddCardContext(null);
        }
    }, [accountCardOptions, newCardId, addCardContext, formattedAccountCardOptions]);

    const handleAddAccountCardFromItem = (itemType, idx, targetField, initialName) => {
        setShowAccountCardInputsEdit(false);
        setAddNewAccountCardDocument(true);
        setAddCardContext({ itemType, idx, targetField });
        setNewAccountCard({
            accountCodeName: initialName || '',
            accountCodeId: cardIdOption,
            accountCaseId: '',
        });
    };

    const handleMuiChangeProxy = (event, value) => {
        setAddCardContext(null);
        if (!value) {
            handleDuplicateItemChange('accountCodeId', null);
            return;
        }
        if (value.value1 === 'AddItem') {
            setAddNewAccountCardDocument(true);
            setShowAccountCardInputsEdit(false);
            setNewAccountCard({
                accountCodeName: (value.searchTerm || searchTerm || ''),
                accountCodeId: cardIdOption,
                accountCaseId: '',
            });
        } else {
            handleDuplicateItemChange('accountCodeId', value.value);
            handleDuplicateItemChange('accountCodeName', value.name);
            handleDuplicateItemChange('accountCaseId', value.accountCaseId);
        }
    };

    const handleNoOptionClick = (searchTerm) => {
        if (formattedAccountCardOptions.length > 0) {
            const newVal = {
                ...formattedAccountCardOptions[0],
                value1: 'AddItem',
                searchTerm
            };
            handleMuiChangeProxy(null, newVal);
        }
    };

    const paymentTypeOptions = [
        { value: "תקבול", label: t('Approval') },
        { value: "תשלום", label: t('Payment') },
    ];

    const inputPropsStyle = {
        disableUnderline: true,
    };

    const inputStyle = {
        style: { textAlign: direction === 'rtl' ? 'right' : 'left' },
    };

    const renderComboPopper = useCallback(
        (props) => <ComboPopper {...props} direction={direction} />,
        [direction]
    );

    const datePickerFieldClassName = `edit-style-input ${paStyles.dateField}`;
    const datePickerInputSx = {
        border: '0 !important',
        borderRadius: '0 !important',
        background: '#f5f5f6 !important',
        height: '40px',
        minHeight: '40px',
        boxSizing: 'border-box',
        boxShadow: 'none !important',
        outline: 'none !important',
        padding: '0 8px',
        fontSize: '15px',
        fontWeight: 500,
        '&:hover, &:focus, &.Mui-focused, &:focus-within': {
            border: '0 !important',
            boxShadow: 'none !important',
            outline: 'none !important',
        },
        '&::before, &::after': {
            display: 'none !important',
            border: '0 !important',
        },
        '& fieldset': {
            border: '0 !important',
        },
        '& input': {
            fontSize: '15px !important',
            fontWeight: 500,
        },
    };

    const renderAccountCardOption = (props, option, onContextMenu) => {
        if (option?.value1 === 'AddItem') {
            const { key, className, ...optionProps } = props;
            return (
                <li
                    key={key}
                    {...optionProps}
                    className={`${className || ''} edit-add-account-option`}
                >
                    <AddAccountCardOptionContent
                        label={t('AddAccountCard')}
                        searchTerm={option.searchTerm || searchTerm}
                    />
                </li>
            );
        }
        return (
            <ClearTooltip
                title={option.isEditable ? t('RightClickToEdit') : t('NotEditable')}
                placement="top"
            >
                <StyledListItem {...props} onContextMenu={onContextMenu}>
                    {`${option?.cardId || ''} - ${option.name}`}
                </StyledListItem>
            </ClearTooltip>
        );
    };

    const renderTableRows = () => {
        if (!duplicateItem) return null;
        return combinedItems.map((row) => {
            const isDisabled = isClosedReportingMonth;
            return (
                <StyledTableRow key={`${row.itemType}-${row.itemTypeIndex}`}>
                    <StyledTableCell className={paStyles.colType}>
                        <ClearTooltip title={t(row.itemType)} placement="top">
                            <div style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '100%'
                            }}>
                                {t(row.itemType)}
                            </div>
                        </ClearTooltip>
                    </StyledTableCell>

                    <StyledTableCell className={paStyles.colDescription}>
                        {row.itemType === 'checks' ? (
                            <TextField
                                className={fieldStyles.styleInput}
                                fullWidth
                                placeholder={t('CheckNumber')}
                                value={row.description || ''}
                                onChange={(e) => handleItemChange('checks', row.itemTypeIndex, 'description', e.target.value)}
                                variant="standard"
                                InputProps={inputPropsStyle}
                                inputProps={inputStyle}
                                disabled={isDisabled}
                            />
                        ) : (
                            <TextField
                                className={fieldStyles.styleInput}
                                fullWidth
                                placeholder={t('Description')}
                                value={row.description || ''}
                                onChange={(e) => handleItemChange(row.itemType, row.itemTypeIndex, 'description', e.target.value)}
                                variant="standard"
                                InputProps={inputPropsStyle}
                                inputProps={inputStyle}
                                disabled={isDisabled}
                            />
                        )}
                    </StyledTableCell>

                    <StyledTableCell className={paStyles.colBankName}>
                        {(row.itemType === 'checks' || row.itemType === 'bankTransactions') ? (
                            <ClearTooltip
                                title={
                                    row.bankName
                                        ? `${row.bankCode || ''} – ${row.bankName}`.replace(/^ – /, '')
                                        : t('BankName')
                                }
                                placement="top"
                            >
                                <span style={{ display: 'block', width: '100%' }}>
                                    <Autocomplete
                                        className={fieldStyles.combo}
                                        size="small"
                                        options={Constants.ISRAEL_BANKS}
                                        getOptionLabel={(o) => `${o.code} – ${o.name}`}
                                        value={
                                            Constants.ISRAEL_BANKS.find(
                                                (b) => b.code === row.bankCode || b.name === row.bankName
                                            ) || null
                                        }
                                        onChange={(_, newVal) => {
                                            handleItemChange(row.itemType, row.itemTypeIndex, 'bankName', newVal ? newVal.name : '');
                                            handleItemChange(row.itemType, row.itemTypeIndex, 'bankCode', newVal ? newVal.code : '');
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                className={fieldStyles.styleInput}
                                                placeholder={t('BankName')}
                                                variant="standard"
                                                InputProps={{
                                                    ...params.InputProps,
                                                    ...inputPropsStyle,
                                                }}
                                                inputProps={{
                                                    ...params.inputProps,
                                                    ...inputStyle,
                                                }}
                                                disabled={isDisabled}
                                            />
                                        )}
                                        isOptionEqualToValue={(o, v) => o.code === v.code}
                                        disabled={isDisabled}
                                        noOptionsText={t('SelectBank')}
                                        PopperComponent={renderComboPopper}
                                        ListboxProps={{ direction }}
                                        renderOption={(props, option) => (
                                            <li {...props} style={{ direction: direction === 'ltr' ? 'ltr' : 'rtl' }}>
                                                {option.code} – {option.name}
                                            </li>
                                        )}
                                        disablePortal={false}
                                    />
                                </span>
                            </ClearTooltip>
                        ) : (
                            <ClearTooltip title="-" placement="top">
                                <span>-</span>
                            </ClearTooltip>
                        )}
                    </StyledTableCell>

                    <StyledTableCell className={paStyles.colBankCode}>
                        {(row.itemType === 'checks' || row.itemType === 'bankTransactions') ? (
                            <ClearTooltip title={row.bankCode || t('BankCode')} placement="top">
                                <TextField
                                    className={fieldStyles.styleInput}
                                    fullWidth
                                    type="number"
                                    placeholder={t('BankCode')}
                                    value={row.bankCode || ''}
                                    onChange={(e) => handleItemChange(row.itemType, row.itemTypeIndex, 'bankCode', e.target.value)}
                                    variant="standard"
                                    InputProps={inputPropsStyle}
                                    inputProps={inputStyle}
                                    disabled
                                />
                            </ClearTooltip>
                        ) : (
                            <ClearTooltip title="-" placement="top">
                                <span>-</span>
                            </ClearTooltip>
                        )}
                    </StyledTableCell>

                    <StyledTableCell className={paStyles.colBankBranch}>
                        {(row.itemType === 'checks' || row.itemType === 'bankTransactions') ? (
                            <ClearTooltip title={row.bankBranchCode || t('BankBranchCode')} placement="top">
                                <TextField
                                    className={fieldStyles.styleInput}
                                    fullWidth
                                    type="number"
                                    placeholder={t('BankBranchCode')}
                                    value={row.bankBranchCode || ''}
                                    onChange={(e) => handleItemChange(row.itemType, row.itemTypeIndex, 'bankBranchCode', e.target.value)}
                                    variant="standard"
                                    InputProps={inputPropsStyle}
                                    inputProps={inputStyle}
                                    disabled={isDisabled}
                                />
                            </ClearTooltip>
                        ) : (
                            <ClearTooltip title="-" placement="top">
                                <span>-</span>
                            </ClearTooltip>
                        )}
                    </StyledTableCell>
                    <StyledTableCell className={paStyles.colBankAccount}>
                        {(row.itemType === 'checks' || row.itemType === 'bankTransactions') ? (
                            <ClearTooltip title={row.supplierAccountNumber || t('SupplierAccountNumber')} placement="top">
                                <TextField
                                    className={fieldStyles.styleInput}
                                    fullWidth
                                    type="number"
                                    placeholder={t('SupplierAccountNumber')}
                                    value={row.supplierAccountNumber || ''}
                                    onChange={(e) => handleItemChange(row.itemType, row.itemTypeIndex, 'supplierAccountNumber', e.target.value)}
                                    variant="standard"
                                    InputProps={inputPropsStyle}
                                    inputProps={inputStyle}
                                    disabled={isDisabled}
                                />
                            </ClearTooltip>
                        ) : (
                            <ClearTooltip title="-" placement="top">
                                <span>-</span>
                            </ClearTooltip>
                        )}
                    </StyledTableCell>

                    <StyledTableCell className={paStyles.colAmount}>
                        <ClearTooltip title={row.amount || t('Amount')} placement="top">
                            <TextField
                                className={fieldStyles.styleInput}
                                fullWidth
                                type="number"
                                placeholder={t('Amount')}
                                value={row.amount ?? ''}
                                onChange={e => handleAmountChange(row.itemType, row.itemTypeIndex, e.target.value)}
                                variant="standard"
                                InputProps={inputPropsStyle}
                                inputProps={inputStyle}
                                disabled={isDisabled}
                            />
                        </ClearTooltip>
                    </StyledTableCell>

                    <StyledTableCell className={paStyles.colCurrencyAmount}>
                        <ClearTooltip title={row.foreignCurrencyAmount || t('CurrencyAmount')} placement="top">
                            <TextField
                                className={fieldStyles.styleInput}
                                fullWidth
                                type="number"
                                placeholder={t('CurrencyAmount')}
                                value={row.foreignCurrencyAmount ?? ''}
                                onChange={(e) => handleForeignAmountChange(row.itemType, row.itemTypeIndex, e.target.value)}
                                variant="standard"
                                InputProps={inputPropsStyle}
                                inputProps={inputStyle}
                                disabled={isDisabled}
                            />
                        </ClearTooltip>
                    </StyledTableCell>

                    <StyledTableCell className={paStyles.colReference2}>
                        <ClearTooltip title={row.reference2 || t('Reference2')} placement="top">
                            <TextField
                                className={fieldStyles.styleInput}
                                fullWidth
                                type="number"
                                placeholder={t('Reference2')}
                                value={row.reference2 || ''}
                                onChange={(e) => handleItemChange(row.itemType, row.itemTypeIndex, 'reference2', e.target.value)}
                                variant="standard"
                                InputProps={inputPropsStyle}
                                inputProps={inputStyle}
                                disabled={isDisabled}
                            />
                        </ClearTooltip>
                    </StyledTableCell>

                    <StyledTableCell className={paStyles.colDueDate}>
                        <ClearTooltip title={row.paymentDate ? formatTooltipDate(row.paymentDate) : t('DuePaymentDate')} placement="top">
                            <span style={{ display: 'block', width: '100%' }}>
                                <CustomDatePicker
                                    date={row.paymentDate ? row.paymentDate : null}
                                    onDateChange={(dateVal) => handlePaymentDateChange(row.itemType, row.itemTypeIndex, dateVal)}
                                    height="40px"
                                    disabled={isDisabled}
                                    textFieldClassName={datePickerFieldClassName}
                                    inputSx={datePickerInputSx}
                                />
                            </span>
                        </ClearTooltip>
                    </StyledTableCell>

                    <StyledTableCell className={paStyles.colAccount}>
                        <ClearTooltip title={formattedAccountCardOptions.find((opt) => opt.value === row.debitAccount)?.label || t('Debit Account')} placement="top">
                            <Autocomplete
                                className={fieldStyles.combo}
                                options={formattedAccountCardOptions}
                                getOptionLabel={(option) => {
                                    if (option?.value1 === 'AddItem') {
                                        return `${t('AddAccountCard')} "${option.searchTerm || searchTerm || ''}"`;
                                    }
                                    return option?.label || '';
                                }}
                                filterOptions={filterAccountCardOptions}
                                value={formattedAccountCardOptions.find((opt) => opt.value === row.debitAccount) || null}
                                onChange={(e, selectedOption) => {
                                    if (selectedOption?.value1 === 'AddItem') {
                                        handleAddAccountCardFromItem(row.itemType, row.itemTypeIndex, 'debitAccount', searchTerm);
                                        return;
                                    }
                                    handleItemChange(
                                        row.itemType,
                                        row.itemTypeIndex,
                                        'debitAccount',
                                        selectedOption ? selectedOption.value : ''
                                    );
                                }}
                                onInputChange={(event, inputValue) => setSearchTerm(inputValue)}
                                disablePortal={false}
                                disableListWrap
                                ListboxComponent={FixedSizeVirtualizedListbox}
                                ListboxProps={{ direction, itemHeight: 36, listHeight: 280 }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        className={fieldStyles.styleInput}
                                        variant="standard"
                                        placeholder={t('Debit Account')}
                                        InputProps={{ ...params.InputProps, ...inputPropsStyle }}
                                        inputProps={{ ...params.inputProps, ...inputStyle }}
                                    />
                                )}
                                renderOption={(props, option) => renderAccountCardOption(props, option, (event) => {
                                    event.preventDefault();
                                    if (!option.isEditable) return;
                                    setAddCardContext({ itemType: row.itemType, idx: row.itemTypeIndex, targetField: 'debitAccount' });
                                    setPalCode(option.palCode);
                                    setNewAccountCard({
                                        accountCodeName: option.name || '',
                                        accountCodeId: option.cardId,
                                        accountCaseId: option.accountCaseId || '',
                                    });
                                    setShowAccountCardInputsEdit(true);
                                    setAddNewAccountCardDocument(true);
                                })}
                                PopperComponent={renderComboPopper}
                                noOptionsText={
                                    <button
                                        type="button"
                                        className="edit-add-account-empty"
                                        onClick={() => handleAddAccountCardFromItem(row.itemType, row.itemTypeIndex, 'debitAccount', searchTerm)}
                                    >
                                        <AddAccountCardOptionContent
                                            label={t('AddAccountCard')}
                                            searchTerm={searchTerm}
                                        />
                                    </button>
                                }
                                disabled={isDisabled}
                            />
                        </ClearTooltip>
                    </StyledTableCell>

                    <StyledTableCell className={paStyles.colAccount}>
                        <ClearTooltip title={formattedAccountCardOptions.find((opt) => opt.value === row.creditAccount)?.label || t('Credit Account')} placement="top">
                            <Autocomplete
                                className={fieldStyles.combo}
                                options={formattedAccountCardOptions}
                                getOptionLabel={(option) => {
                                    if (option?.value1 === 'AddItem') {
                                        return `${t('AddAccountCard')} "${option.searchTerm || searchTerm || ''}"`;
                                    }
                                    return option?.label || '';
                                }}
                                filterOptions={filterAccountCardOptions}
                                value={formattedAccountCardOptions.find((opt) => opt.value === row.creditAccount) || null}
                                onChange={(e, selectedOption) => {
                                    if (selectedOption?.value1 === 'AddItem') {
                                        handleAddAccountCardFromItem(row.itemType, row.itemTypeIndex, 'creditAccount', searchTerm);
                                        return;
                                    }
                                    handleItemChange(
                                        row.itemType,
                                        row.itemTypeIndex,
                                        'creditAccount',
                                        selectedOption ? selectedOption.value : ''
                                    );
                                }}
                                onInputChange={(event, inputValue) => setSearchTerm(inputValue)}
                                disablePortal={false}
                                disableListWrap
                                ListboxComponent={FixedSizeVirtualizedListbox}
                                ListboxProps={{ direction, itemHeight: 36, listHeight: 280 }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        className={fieldStyles.styleInput}
                                        variant="standard"
                                        placeholder={t('Credit Account')}
                                        InputProps={{ ...params.InputProps, ...inputPropsStyle }}
                                        inputProps={{ ...params.inputProps, ...inputStyle }}
                                    />
                                )}
                                renderOption={(props, option) => renderAccountCardOption(props, option, (event) => {
                                    event.preventDefault();
                                    if (!option.isEditable) return;
                                    setAddCardContext({ itemType: row.itemType, idx: row.itemTypeIndex, targetField: 'creditAccount' });
                                    setPalCode(option.palCode);
                                    setNewAccountCard({
                                        accountCodeName: option.name || '',
                                        accountCodeId: option.cardId,
                                        accountCaseId: option.accountCaseId || '',
                                    });
                                    setShowAccountCardInputsEdit(true);
                                    setAddNewAccountCardDocument(true);
                                })}
                                PopperComponent={renderComboPopper}
                                noOptionsText={
                                    <button
                                        type="button"
                                        className="edit-add-account-empty"
                                        onClick={() => handleAddAccountCardFromItem(row.itemType, row.itemTypeIndex, 'creditAccount', searchTerm)}
                                    >
                                        <AddAccountCardOptionContent
                                            label={t('AddAccountCard')}
                                            searchTerm={searchTerm}
                                        />
                                    </button>
                                }
                                disabled={isDisabled}
                            />
                        </ClearTooltip>
                    </StyledTableCell>

                    <StyledTableCell className={paStyles.colActions}>
                        {!isDisabled && (
                            <ClearTooltip title={t('Delete')} placement="top">
                                <Button
                                    style={{ color: 'red', minWidth: '20px' }}
                                    onClick={() => handleRemoveItem(row.itemType, row.itemTypeIndex)}
                                    size="small"
                                >
                                    <DeleteIcon style={{ fontSize: '18px' }} />
                                </Button>
                            </ClearTooltip>
                        )}
                    </StyledTableCell>
                </StyledTableRow>
            );
        });
    };

    // Gross − withholding = net; the payment lines have to add up to the net.
    const amounts = useMemo(
        () => computePaymentAmounts({
            amount: duplicateItem?.amount,
            withholdingTax: duplicateItem?.withholdingTax,
            items: combinedItems,
        }),
        [duplicateItem?.amount, duplicateItem?.withholdingTax, combinedItems]
    );

    const deductionRatePercent = useMemo(
        () => readDeductionRate(accountCardOptions?.find((opt) => opt.cardId === duplicateItem?.accountCodeId)),
        [accountCardOptions, duplicateItem?.accountCodeId]
    );

    const withholdingRow = usePaymentWithholdingRow({
        withholdingTax: duplicateItem?.withholdingTax,
        deductionRatePercent,
        amounts,
        onWithholdingChange: (value) => handleDuplicateItemChange('withholdingTax', value),
    });

    // Validation
    const missingFields = useMemo(() => {
        if (!duplicateItem) return [];
        const missing = [];
        const isIncome = duplicateItem.paymentAcceptanceType === 'תקבול';
        const clientOrProviderLabel = isIncome ? t('Client Name') : t('Provider Name');
        if (!duplicateItem.accountCodeId) missing.push(clientOrProviderLabel);
        if (!duplicateItem.paymentAcceptanceType) missing.push(t('PaymentType'));
        if (!duplicateItem.date) missing.push(t('ReceiptPaymentDate'));
        if (!duplicateItem.uploadDate) missing.push(t('RegistrationDate'));

        combinedItems.forEach((row, idx) => {
            const rowPrefix = `${t('Type')}: ${t(row.itemType)} #${idx + 1}`;
            if (row.amount == null || row.amount === '') missing.push(`${rowPrefix} - ${t('Amount')}`);
            if (!row.debitAccount) missing.push(`${rowPrefix} - ${t('Debit Account')}`);
            if (!row.creditAccount) missing.push(`${rowPrefix} - ${t('Credit Account')}`);
        });

        if (amounts.hasMismatch) {
            missing.push(
                `${t('AmountDoesNotMatchDocument')} ` +
                `(${t('TotalNet')}: ${beautifyNumber(amounts.net).trim()} ≠ ${t('LinesTotal')}: ${beautifyNumber(amounts.linesTotal).trim()})`
            );
        }

        if (isPartialPayment) {
            const paidNow = toAmount(duplicateItem.amount);
            if (paidNow <= 0) {
                missing.push(t('PartialPaymentAmount'));
            } else if (paidNow - openBalance > 0.01) {
                missing.push(t('PartialAmountExceedsOpenBalance'));
            }
        }
        return missing;
    }, [duplicateItem, combinedItems, t, amounts, isPartialPayment, openBalance]);

    const isSaveDisabled = missingFields.length > 0;

    // A partial payment says so on the document itself, so the accountant sees at a glance
    // how much of the invoice this receipt/payment covers.
    const handleSave = () => {
        if (!isPartialPayment || documentTotal <= 0) {
            onSave(duplicateItem);
            return;
        }
        const note = buildPartialPaymentNote(duplicateItem.amount, documentTotal);
        const description = duplicateItem.description || '';
        if (!note || description.includes(PARTIAL_PAYMENT_NOTE_MARKER)) {
            onSave(duplicateItem);
            return;
        }
        onSave({
            ...duplicateItem,
            description: description ? `${description} – ${note}` : note,
        });
    };

    if (!open || !duplicateItem) return null;

    const noPdf = !showPdfIframe;
    const halfFieldClass = noPdf ? paStyles.span2 : undefined;
    const wrapFieldStyle = showPdfIframe ? { width: '48%' } : undefined;

    return (
        <Portal container={container}>
            <Draggable handle=".handle" nodeRef={nodeRef}>
                <div
                    ref={nodeRef}
                    className={`edit-modal-shell size-medium pa-duplicate-modal${showPdfIframe ? ' with-pdf' : ''} ${direction === 'rtl' ? fieldStyles.rtl : fieldStyles.ltr}`}
                    style={{
                        position: 'absolute',
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        padding: '12px',
                        zIndex: 1300,
                        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.2)',
                        border: '1px solid #304fff',
                        overflowY: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        direction: direction,
                    }}
                >
                    {/* ---------- HEADER (Draggable handle) ---------- */}
                    <div
                        className="handle"
                        style={{
                            cursor: 'move',
                            fontSize: '18px',
                            fontWeight: 'bold',
                            marginBottom: '10px',
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Tooltip title={t('Exit')}>
                            <IconButton onClick={onClose}>
                                <CancelIcon style={{ color: '#304FFF' }} />
                            </IconButton>
                        </Tooltip>

                        {/* Title (centered) */}
                        <div
                            className="edit-title-container"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                margin: '0 auto',
                            }}
                        >
                            <div className="edit-title">
                                {t('DuplicatePayment')}
                            </div>
                        </div>

                        {/* Toggle PDF visibility */}
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            {allDocUrls.length > 0 && (
                                <IconButton onClick={handleToggleIframe}>
                                    {showPdfIframe ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                </IconButton>
                            )}
                        </div>
                    </div>

                    {/* ===================== TOP ROW: Side-by-side ===================== */}
                    <div style={{ display: 'flex', flexDirection: 'row', width: '100%' }}>
                        {/* ===================== LEFT SIDE (Details) ===================== */}
                        <div
                            style={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                marginRight: '12px',
                            }}
                        >
                            {/* Receipt Details */}
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px', gap: '6px' }}>
                                <h3 className={paStyles.sectionTitle} style={{ margin: 0 }}>{t('ReceiptDetails')}</h3>
                            </div>

                            <div
                                className={noPdf ? paStyles.detailsGrid : undefined}
                                style={showPdfIframe ? { display: 'flex', flexWrap: 'wrap', gap: '8px' } : undefined}
                            >
                        <div className={halfFieldClass} style={wrapFieldStyle}>
                            <div style={{ fontWeight: 'bold', marginBottom: '2px', fontSize: '13px' }}>
                                {duplicateItem.paymentAcceptanceType === 'תקבול'
                                    ? t('Client Name')
                                    : t('Provider Name')}
                                <span style={{ color: 'red' }}>*</span>
                            </div>
                            <Autocomplete
                                className={fieldStyles.combo}
                                options={formattedAccountCardOptions}
                                getOptionLabel={(option) => {
                                    if (option?.value1 === 'AddItem') {
                                        return `${t('AddAccountCard')} "${option.searchTerm || searchTerm || ''}"`;
                                    }
                                    return option?.label || '';
                                }}
                                filterOptions={filterAccountCardOptions}
                                value={
                                    formattedAccountCardOptions.find(
                                        (option) => option.value === duplicateItem.accountCodeId
                                    ) || null
                                }
                                onChange={handleMuiChangeProxy}
                                onInputChange={(event, inputValue) => setSearchTerm(inputValue)}
                                disablePortal={false}
                                disableListWrap
                                ListboxComponent={FixedSizeVirtualizedListbox}
                                ListboxProps={{ direction, itemHeight: 40, listHeight: 320 }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        className={fieldStyles.styleInput}
                                        placeholder={
                                            duplicateItem.paymentAcceptanceType === 'תקבול'
                                                ? t('Client Name')
                                                : t('Provider Name')
                                        }
                                        variant="standard"
                                        required
                                        InputProps={{ ...params.InputProps, ...inputPropsStyle }}
                                        inputProps={{ ...params.inputProps, ...inputStyle }}
                                    />
                                )}
                                renderOption={(props, option) => renderAccountCardOption(props, option, (event) => {
                                    event.preventDefault();
                                    if (!option.isEditable) return;
                                    setPalCode(option.palCode);
                                    setNewAccountCard({
                                        accountCodeName: option.name || '',
                                        accountCodeId: option.cardId,
                                        accountCaseId: option.accountCaseId || '',
                                    });
                                    setShowAccountCardInputsEdit(true);
                                    setAddNewAccountCardDocument(true);
                                })}
                                PopperComponent={renderComboPopper}
                                noOptionsText={
                                    <button
                                        type="button"
                                        className="edit-add-account-empty"
                                        onClick={() => handleNoOptionClick(searchTerm)}
                                    >
                                        <AddAccountCardOptionContent
                                            label={t('AddAccountCard')}
                                            searchTerm={searchTerm}
                                        />
                                    </button>
                                }
                            />
                        </div>

                        <div className={halfFieldClass} style={wrapFieldStyle}>
                            <div style={{ fontWeight: 'bold', marginBottom: '2px', fontSize: '13px' }}>
                                {t('PaymentType')}
                                <span style={{ color: 'red' }}>*</span>
                            </div>
                            <Autocomplete
                                className={fieldStyles.combo}
                                options={paymentTypeOptions}
                                getOptionLabel={(option) => option.label}
                                value={
                                    paymentTypeOptions.find(
                                        (option) => option.value === duplicateItem.paymentAcceptanceType
                                    ) || null
                                }
                                onChange={(e, newValue) =>
                                    handleDuplicateItemChange('paymentAcceptanceType', newValue ? newValue.value : '')
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        className={fieldStyles.styleInput}
                                        variant="standard"
                                        placeholder={t('SelectPaymentType')}
                                        InputProps={{ ...params.InputProps, ...inputPropsStyle }}
                                        inputProps={{ ...params.inputProps, ...inputStyle }}
                                    />
                                )}
                                PopperComponent={renderComboPopper}
                            />
                        </div>

                        <div className={halfFieldClass} style={wrapFieldStyle}>
                            <div style={{ fontWeight: 'bold', marginBottom: '2px', fontSize: '13px' }}>
                                {t('ReceiptPaymentDate')}
                                <span style={{ color: 'red' }}>*</span>
                            </div>
                            <CustomDatePicker
                                date={duplicateItem.date ? duplicateItem.date : null}
                                onDateChange={(date) => {
                                    if (date) {
                                        handleDuplicateItemChange('date', date);
                                    }
                                }}
                                height="40px"
                                textFieldClassName={datePickerFieldClassName}
                                inputSx={datePickerInputSx}
                            />
                        </div>

                        <div className={halfFieldClass} style={wrapFieldStyle}>
                            <div style={{ fontWeight: 'bold', marginBottom: '2px', fontSize: '13px' }}>
                                {t('RegistrationDate')}
                                <span style={{ color: 'red' }}>*</span>
                            </div>
                            <CustomDatePicker
                                date={duplicateItem.uploadDate ? duplicateItem.uploadDate : null}
                                onDateChange={(date) => {
                                    if (date) {
                                        handleDuplicateItemChange('uploadDate', date);
                                    }
                                }}
                                height="40px"
                                textFieldClassName={datePickerFieldClassName}
                                inputSx={datePickerInputSx}
                            />
                        </div>

                        <div style={wrapFieldStyle}>
                            <div className="edit-form-row-item-title" style={{ fontWeight: 'bold', fontSize: '13px', marginBottom: '2px' }}>
                                {t('MonthReportDate')}
                            </div>
                            <CustomMonthDatePicker
                                date={
                                    duplicateItem.reportingMonthDate
                                        ? duplicateItem.reportingMonthDate
                                        : null
                                }
                                onDateChange={(date) => {
                                    if (date) {
                                        handleDuplicateItemChange('reportingMonthDate', date);
                                    }
                                }}
                                height="40px"
                                direction={direction}
                            />
                        </div>
                        <div style={wrapFieldStyle}>
                            <div className="edit-form-row-item-title" style={{ fontWeight: 'bold', fontSize: '13px', marginBottom: '2px' }}>
                                {t('RegistrationMonthDate')}
                            </div>
                            <CustomMonthDatePicker
                                date={
                                    duplicateItem.registrationMonthDate
                                        ? duplicateItem.registrationMonthDate
                                        : null
                                }
                                onDateChange={(date) => {
                                    if (date) {
                                        handleDuplicateItemChange('registrationMonthDate', date);
                                    }
                                }}
                                height="40px"
                                direction={direction}
                            />
                        </div>

                        <div className={halfFieldClass} style={wrapFieldStyle}>
                            <div style={{ fontWeight: 'bold', marginBottom: '2px', fontSize: '13px' }}>
                                {t('ReceiptPaymentDescription')}
                            </div>
                            <TextField
                                className={fieldStyles.styleInput}
                                fullWidth
                                type="text"
                                placeholder={t('ReceiptPaymentDescription')}
                                value={duplicateItem.description || ''}
                                onChange={(e) => handleDuplicateItemChange('description', e.target.value)}
                                variant="standard"
                                InputProps={inputPropsStyle}
                                inputProps={inputStyle}
                            />
                        </div>

                        <div className={halfFieldClass} style={wrapFieldStyle}>
                            <div style={{ fontWeight: 'bold', marginBottom: '2px', fontSize: '13px' }}>
                                {t('Reference')}
                            </div>
                            <TextField
                                className={fieldStyles.styleInput}
                                fullWidth
                                placeholder={t('Reference')}
                                value={duplicateItem.reference || ''}
                                onChange={(event) =>
                                    handleDuplicateItemChange('reference', event.target.value)
                                }
                                variant="standard"
                                InputProps={inputPropsStyle}
                                inputProps={inputStyle}
                            />
                        </div>

                        <div className={halfFieldClass} style={wrapFieldStyle}>
                            <div style={{ fontWeight: 'bold', marginBottom: '2px', fontSize: '13px' }}>
                                {t('Currency')}
                            </div>
                            <Autocomplete
                                className={fieldStyles.combo}
                                options={Object.entries(Constants.ForeignCurrencyCode).map(([code, num]) => ({
                                    code,
                                    value: num,
                                    label: `${code} ${Constants.ForeignCurrencySymbol[code]}`,
                                }))}
                                getOptionLabel={(option) => (option && option.label) ?? ''}
                                isOptionEqualToValue={(option, value) =>
                                    option != null && value != null && option.value === value.value
                                }
                                value={
                                    duplicateItem.foreignCurrencyCode != null &&
                                    duplicateItem.foreignCurrencyCode !== ''
                                        ? (() => {
                                              const fc = duplicateItem.foreignCurrencyCode;
                                              const code =
                                                  typeof fc === 'string' && Constants.ForeignCurrencyCode[fc] !== undefined
                                                      ? fc
                                                      : Object.keys(Constants.ForeignCurrencyCode).find(
                                                            (k) => Constants.ForeignCurrencyCode[k] === fc
                                                        );
                                              if (!code) return null;
                                              const numVal = typeof fc === 'number' ? fc : Constants.ForeignCurrencyCode[code];
                                              return {
                                                  code,
                                                  value: numVal,
                                                  label: `${code} ${Constants.ForeignCurrencySymbol[code] ?? ''}`,
                                              };
                                          })()
                                        : null
                                }
                                onChange={(e, newVal) =>
                                    handleCurrencyCodeChange(newVal ? newVal.value : null)
                                }
                                disableListWrap
                                ListboxComponent={FixedSizeVirtualizedListbox}
                                ListboxProps={{ direction: 'rtl', itemHeight: 40, listHeight: 240 }}
                                renderInput={(params) => (
                                    <TextField
                                        className={fieldStyles.styleInput}
                                        {...params}
                                        variant="standard"
                                        placeholder={t('Currency')}
                                        InputProps={{
                                            ...params.InputProps,
                                            ...inputPropsStyle,
                                        }}
                                        inputProps={{
                                            ...params.inputProps,
                                            style: { textAlign: 'right', direction: 'rtl' },
                                        }}
                                    />
                                )}
                                PopperComponent={renderComboPopper}
                                disabled={isClosedReportingMonth}
                            />
                        </div>
                            </div>

                            {/* -------- Divider -------- */}
                            <div
                                style={{
                                    borderTop: '1px solid #e5e7f6',
                                    margin: '6px 0',
                                    width: '96%',
                                }}
                            />

                            <h3 className={paStyles.sectionTitle}>{t('ReceiptPaymentItems')}</h3>

                            {!isClosedReportingMonth && (
                                <div className={paStyles.addItemsRow}>
                            <Button
                                onClick={() => handleAddItem('checks')}
                                className={paStyles.addItemBtn}
                            >
                                {t('AddCheck')}
                            </Button>
                            <Button
                                onClick={() => handleAddItem('creditCards')}
                                className={paStyles.addItemBtn}
                            >
                                {t('AddCreditCard')}
                            </Button>
                            <Button
                                onClick={() => handleAddItem('cash')}
                                className={paStyles.addItemBtn}
                            >
                                {t('AddCash')}
                            </Button>
                            <Button
                                onClick={() => handleAddItem('bankTransactions')}
                                className={paStyles.addItemBtn}
                            >
                                {t('AddBankTransfer')}
                            </Button>
                            <Button
                                onClick={() => handleAddItem('offsets')}
                                className={paStyles.addItemBtn}
                            >
                                {t('AddOffset')}
                            </Button>
                            <Button
                                onClick={withholdingRow.addRow}
                                disabled={withholdingRow.isVisible}
                                sx={{
                                    background: '#F9AA2A',
                                    color: '#fff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    fontSize: '13px',
                                    padding: '6px 12px',
                                    '&:hover': {
                                        background: '#E09520',
                                    },
                                    '&.Mui-disabled': {
                                        background: '#E0E0E0',
                                        color: '#BDBDBD',
                                    }
                                }}
                            >
                                {t('AddWithholdingTax')}
                            </Button>
                                </div>
                            )}
                        </div>

                        {/* ===================== RIGHT SIDE (PDF Preview) ===================== */}
                        {(showPdfIframe && allDocUrls.length > 0) && (
                            <div
                                style={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    minHeight: '300px',
                                    padding: '8px',
                                    border: '1px solid #ddd',
                                    borderRadius: '6px',
                                    backgroundColor: '#f9f9f9',
                                    position: 'relative',
                                }}
                            >
                                {allDocUrls.length > 1 && (
                                    <FilePreviewCarousel
                                        allDocUrls={allDocUrls}
                                        docIndex={docIndex}
                                        setDocIndex={setDocIndex}
                                        t={t}
                                    />
                                )}

                                {/* Main Document View */}
                                <Iframe
                                    url={
                                        allDocUrls.length > 0
                                            ? `${allDocUrls[docIndex]}#page=1&view=FitH`
                                            : `${NoDocumentsPdf}#page=1&view=FitH`
                                    }
                                    width="100%"
                                    height="100%"
                                    id="receipt-pdf"
                                    title="Receipt PDF"
                                    allowFullScreen
                                    style={{
                                        marginTop: '8px',
                                        borderRadius: '6px',
                                        border: '1px solid #ddd',
                                        backgroundColor: '#fff',
                                    }}
                                />
                            </div>
                        )}
                    </div>

                    <div className={`${paStyles.tableSection} ${paStyles.modal_medium}`}>
                        <PaymentPartialAmountPanel
                            t={t}
                            documentTotal={documentTotal}
                            alreadyPaid={alreadyPaid}
                            amount={duplicateItem.amount}
                            isPartial={isPartialPayment}
                            onPartialToggle={handlePartialToggle}
                            onAmountChange={handlePaidAmountChange}
                            disabled={isClosedReportingMonth}
                        />

                        <PaymentAmountsSummaryBar
                            t={t}
                            amounts={amounts}
                            disabled={isClosedReportingMonth}
                            onGrossChange={handlePaidAmountChange}
                        />

                        <div className={paStyles.tableWrap}>
                        <StyledTableContainer component={Paper}>
                        <StyledTable stickyHeader className={paStyles.itemsTable}>
                            <StyledTableHead>
                                <TableRow>
                                    <StyledTableCellHeader className={paStyles.colType}>{t('Type')}</StyledTableCellHeader>
                                    <StyledTableCellHeader className={paStyles.colDescription}>
                                        {t('CheckNumber')} / {t('Description')}
                                    </StyledTableCellHeader>
                                    <StyledTableCellHeader className={paStyles.colBankName}>{t('BankName')}</StyledTableCellHeader>
                                    <StyledTableCellHeader className={paStyles.colBankCode}>{t('BankCode')}</StyledTableCellHeader>
                                    <StyledTableCellHeader className={paStyles.colBankBranch}>{t('BankBranchCode')}</StyledTableCellHeader>
                                    <StyledTableCellHeader className={paStyles.colBankAccount}>{t('BankAccountNumber')}</StyledTableCellHeader>
                                    <StyledTableCellHeader className={paStyles.colAmount}>{t('Amount')}</StyledTableCellHeader>
                                    <StyledTableCellHeader className={paStyles.colCurrencyAmount}>{t('CurrencyAmount')}</StyledTableCellHeader>
                                    <StyledTableCellHeader className={paStyles.colReference2}>{t('Reference2')}</StyledTableCellHeader>
                                    <StyledTableCellHeader className={paStyles.colDueDate}>{t('DuePaymentDate')}</StyledTableCellHeader>
                                    <StyledTableCellHeader className={paStyles.colAccount}>{t('Debit Account')}</StyledTableCellHeader>
                                    <StyledTableCellHeader className={paStyles.colAccount}>{t('Credit Account')}</StyledTableCellHeader>
                                    <StyledTableCellHeader className={paStyles.colActions}>{t('Actions')}</StyledTableCellHeader>
                                </TableRow>
                            </StyledTableHead>
                            <TableBody>
                                {renderTableRows()}
                                {withholdingRow.isVisible && (
                                    <PaymentWithholdingTaxRow
                                        t={t}
                                        direction={direction}
                                        inputPropsStyle={inputPropsStyle}
                                        inputStyle={inputStyle}
                                        disabled={isClosedReportingMonth}
                                        {...withholdingRow.rowProps}
                                    />
                                )}
                            </TableBody>
                        </StyledTable>
                    </StyledTableContainer>
                        </div>
                    </div>

                    {/* Footer Buttons */}
                    <div className="form-row" style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                        <Button
                            variant="outlined"
                            onClick={onClose}
                            sx={{
                                borderColor: '#ccc',
                                color: '#666',
                            }}
                        >
                            {t('cancel')}
                        </Button>
                        <Tooltip
                            title={
                                isSaveDisabled && missingFields.length > 0
                                    ? `${t('PleaseFillRequiredFields')}: ${missingFields.join(', ')}`
                                    : t('Save')
                            }
                            placement="top"
                        >
                            <span>
                                <Button
                                    variant="contained"
                                    onClick={handleSave}
                                    disabled={isSaveDisabled}
                                    sx={{
                                        backgroundColor: isSaveDisabled ? '#ccc' : '#304FFF',
                                        color: 'white',
                                        '&:hover': {
                                            backgroundColor: isSaveDisabled ? '#ccc' : '#1E40FF',
                                        },
                                        '&:disabled': {
                                            backgroundColor: '#E0E0E0',
                                            color: '#BDBDBD',
                                        }
                                    }}
                                >
                                    {t('Save')}
                                </Button>
                            </span>
                        </Tooltip>
                    </div>

                    {/* Add Account Card Modal */}
                    <AddAccountCardModal
                        accountCardOptions={accountCardOptions}
                        open={addNewAccountCardDocument}
                        onClose={() => {
                            setAddNewAccountCardDocument(false);
                            setShowAccountCardInputsEdit(false);
                        }}
                        editedItem={newAccountCard}
                        isEdit={showAccountCardInputsEdit}
                        isRange={isRange}
                        isClosedReportingMonth={isClosedReportingMonth}
                        palCodes={Constants.AccountCardPalCode}
                        propPalCode={palCode}
                        setPalCode={setPalCode}
                        direction={direction}
                        isAdd={!showAccountCardInputsEdit}
                        client={client}
                        token={token}
                        setLoading={setLoading}
                        handleChangeAfterAdd={handleChangeAfterAdd}
                        fetchEnums={fetchEnums}
                        type="main"
                    />
                </div>
            </Draggable>
        </Portal>
    );
};

export default PaymentAcceptanceDuplicateModal;

